import React, { useState, useRef, useEffect, useMemo } from 'react';
import { formatBytes, formatDateTime, getIntlLocale } from '../../../utils/functions';
import { documentUploadedAt, resolveUploader } from '../../../utils/documentMeta';
import { getJurisdictionDisplay } from '../../../utils/jurisdictionFlag';
import { languageName } from '../../../utils/languageName';
import { InfoRow, Well } from '../brand/Field';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '../../../store';
import { hideSidePanel } from '../../../store/actions/sidePanelActions';
import { showModal, hideModal } from '../../../store/actions/modalActions';
import { showFeedbackModal } from '../../../store/actions/UserFeedbackActions';
import { removeSourceFromMatter } from '../../../store/actions/Matters/MatterActions';
import { PiTrashLight, PiXLight, PiParagraphLight, PiGlobeLight, PiArrowSquareOutLight, PiFileLight, PiBookOpenLight, PiScalesLight, PiClipboardTextLight, PiChatCenteredTextLight } from 'react-icons/pi';
import Spinner from './Spinner';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { motion, AnimatePresence } from 'framer-motion';
import Skeleton from './Skeleton';
import EmailMessageView from './EmailMessageView';
import { getUserType } from '../../../utils/authUtils';
import Button from './Button';
import FileTypeSkeleton from '../../Files/documents/FileTypeSkeleton';
import { useFeatureTour } from '../../../hooks/useFeatureTour';
import { FeatureTour, sourceReaderTourSteps } from '../FeatureTour';
import { normalizeSourceUrl } from '../../../utils/sourceUrl';

interface CustomFileReaderProps {
  citation: {
    title: string;
    name: string;
    cdn_url?: string;
    blob_url?: string;
    external_url?: string;
    relevant_sections?: string | string[];
    summary?: string;
    justification?: string;
    content?: string;
    type?: 'file' | 'law' | 'case' | 'document';
    size?: number;
    created_at?: string;
    uploaded_at?: string;
    lastModified?: string;
    source?: string;
    id?: string;
    source_id?: string;
    country?: string;
    [key: string]: any;
  };
  matterId: string;
  sourceType: 'file' | 'law' | 'case' | 'document';
  userAccessLevel?: 'edit' | 'admin' | 'view';
  onClose?: () => void;
  onDelete?: () => void;
}

/**
 * Corpus → a human label for the reader's "Type" fact.
 *
 * A local copy of the matter source model's labels rather than an import: this
 * component is shared chrome and must not depend on the Matters feature tree.
 * Untranslated - the caller runs them through `t`.
 */
const CORPUS_LABEL: Record<string, string> = {
  case_file: 'Case file',
  legislation: 'Legislation',
  case_law: 'Case law',
  preparatory_works: 'Preparatory works',
  regulations: 'Regulations',
  agency_decisions: 'Agency decisions',
  data_sources: 'Data source',
};

// Strip data-file extensions (.json, .txt, .xml, .csv, .html, .htm) from external
// URLs so the user is directed to the source provider's web page, not a raw file.
// Also applies provider-specific fixes (e.g. wetten.overheid.nl /id/ removal).
const cleanExternalUrl = (url: string): string => {
  try {
    const u = new URL(normalizeSourceUrl(url));
    u.pathname = u.pathname.replace(/\.(json|txt|xml|csv|html?)$/i, '');
    return u.toString();
  } catch {
    return normalizeSourceUrl(url);
  }
};

// Shared style for the structured readable readers (JSON + XML) so both render
// legal data as labelled sections/fields instead of a raw markup dump.
const STRUCTURED_READER_STYLE = `<style>
  .json-reader * { font-family: inherit; box-sizing: border-box; }
  .json-reader { font-size: 0.875rem; line-height: 1.7; color: #1a1a1a; max-width: 100%; padding: 0.5rem; }
  .dark .json-reader { color: #e5e7eb; }
  .json-reader .json-section { margin: 16px 0; }
  .json-reader .json-label {
    font-size: 0.8rem; font-weight: 600; color: #555; text-transform: uppercase;
    letter-spacing: 0.05em; margin: 0 0 6px 0; padding: 6px 10px;
    background: #f5f5f5; border-left: 3px solid #999; border-radius: 0 4px 4px 0;
  }
  .dark .json-reader .json-label { color: #9ca3af; background: #1f2937; border-left-color: #6b7280; }
  .json-reader .json-content { padding: 0 0 0 12px; }
  .json-reader .json-long-text { white-space: pre-wrap; line-height: 1.8; color: #333; }
  .dark .json-reader .json-long-text { color: #d1d5db; }
  .json-reader .json-field {
    display: flex; gap: 12px; padding: 6px 10px; border-bottom: 1px solid #f0f0f0;
    align-items: baseline;
  }
  .dark .json-reader .json-field { border-bottom-color: #374151; }
  .json-reader .json-field-label { font-weight: 600; color: #555; min-width: 140px; flex-shrink: 0; font-size: 0.8rem; }
  .dark .json-reader .json-field-label { color: #9ca3af; }
  .json-reader .json-field-value { color: #222; word-break: break-word; }
  .dark .json-reader .json-field-value { color: #e5e7eb; }
  .json-reader .json-list { list-style: none; margin: 4px 0; padding: 0; }
  .json-reader .json-list li { position: relative; padding: 2px 0 2px 16px; margin: 2px 0; }
  .json-reader .json-list li::before { content: "–"; position: absolute; left: 0; color: #999; }
</style>`;

// Site-chrome selectors to strip from raw source HTML (wetten.overheid.nl et al.)
// so we render the legal content, not navigation/breadcrumbs/action toolbars.
const HTML_JUNK_SELECTORS = [
  'script', 'style', 'noscript', 'nav', 'header', 'footer', 'aside',
  'form', 'input', 'button', 'select', 'textarea', 'iframe',
  '.skiplinks', '.breadcrumb', '.header__more', '#other-sites', '.row--page-opener',
  '[role="navigation"]', '[role="search"]', '.actionblock', '[class*="action--"]',
  'ul[aria-label^="Lijst met mogelijke acties"]', '.columns--sticky-sidebar__sidebar', '[hidden]',
].join(',');

// Resolve a possibly-relative href/src against the source's own origin.
const absolutizeUrl = (href: string, origin: string): string => {
  if (!href) return href;
  if (href.startsWith('#')) return href;
  if (/^(https?:|mailto:|tel:|data:)/i.test(href)) return href;
  if (!origin) return href;
  try { return new URL(href, origin).toString(); } catch { return href; }
};

// Clean raw source HTML: drop chrome, keep the main content region, and make
// every link absolute + open-in-new-tab so it never hijacks the SPA.
const sanitizeSourceHtml = (rawText: string, origin: string): string => {
  const doc = new DOMParser().parseFromString(rawText, 'text/html');
  // Pick the main content region FIRST - on wetten it lives inside a <form>,
  // so removing junk (form/header/...) before extracting would delete it.
  const main = doc.querySelector('#content, #regeling, main, article') || doc.body;
  if (!main) return '';
  // Strip site chrome / toolbars from within the content region.
  main.querySelectorAll(HTML_JUNK_SELECTORS).forEach(el => el.remove());
  main.querySelectorAll('a[href]').forEach(a => {
    const href = a.getAttribute('href') || '';
    if (href.startsWith('#')) return; // in-page anchor - handled by the click interceptor
    a.setAttribute('href', normalizeSourceUrl(absolutizeUrl(href, origin)));
    a.setAttribute('target', '_blank');
    a.setAttribute('rel', 'noopener noreferrer');
  });
  main.querySelectorAll('img[src]').forEach(img => {
    img.setAttribute('src', absolutizeUrl(img.getAttribute('src') || '', origin));
  });
  return main.innerHTML;
};

const CustomFileReader: React.FC<CustomFileReaderProps> = ({ citation, matterId, sourceType, userAccessLevel, onClose, onDelete }) => {
  const [isLoading, setIsLoading] = useState(true);
  // True only once we've genuinely stopped waiting for a streamed blob (the
  // timeout below fired). Until then we keep showing the loader rather than the
  // "No content available" empty state - the bug where a document that is still
  // streaming briefly flashed "No content available" before it rendered.
  const [streamGaveUp, setStreamGaveUp] = useState(false);
  const [documentHtml, setDocumentHtml] = useState<string>('');
  const [isConvertingWord, setIsConvertingWord] = useState(false);
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const canEdit = userAccessLevel === 'edit' || userAccessLevel === 'admin';

  const containerRef = useRef<HTMLDivElement | null>(null);
  const pdfRef = useRef<HTMLDivElement | null>(null);
  const documentContentRef = useRef<HTMLDivElement | null>(null);

  const {
    isRunning: isTourRunning,
    handleJoyrideCallback: handleTourCallback,
  } = useFeatureTour({
    tourId: 'source-reader',
    steps: sourceReaderTourSteps,
    readyToShow: !isLoading,
    autoStartDelay: 600,
  });

  // Convert blob to object URL, keep string URLs as-is
  const fileSource = useMemo(() => {
    if (citation.blob instanceof Blob) {
      return URL.createObjectURL(citation.blob);
    }
    if (citation.blobObject instanceof Blob) {
      return URL.createObjectURL(citation.blobObject);
    }
    if (citation.blob_url) return citation.blob_url;
    if (citation.cdn_url) return citation.cdn_url;
    if (citation.external_url?.toLowerCase().endsWith('.pdf')) return citation.external_url;
    return null;
  }, [citation.blob, citation.blobObject, citation.blob_url, citation.cdn_url, citation.external_url]);

  // Origin of the source itself (e.g. https://wetten.overheid.nl) - used to
  // absolutize relative links in rendered source HTML so they resolve correctly.
  const sourceOrigin = useMemo(() => {
    const raw = citation.external_url || citation.url || citation.blob_url || citation.cdn_url || '';
    try { return raw ? new URL(normalizeSourceUrl(raw)).origin : ''; } catch { return ''; }
  }, [citation.external_url, citation.url, citation.blob_url, citation.cdn_url]);

  const getDocumentType = () => {
    const contentType = citation.contentType || '';
    const url = fileSource || '';
    const blobPath = citation.blobPath || citation.blob_path || '';

    if (url.includes('.pdf') || contentType.includes('pdf') || blobPath.endsWith('.pdf')) return 'pdf';
    if (contentType.includes('word') || contentType.includes('document') || url.includes('.docx') || blobPath.endsWith('.docx')) return 'word';
    if (contentType.includes('sheet') || url.includes('.xlsx') || blobPath.endsWith('.xlsx')) return 'excel';
    if (contentType.includes('presentation') || url.includes('.pptx') || blobPath.endsWith('.pptx')) return 'powerpoint';
    if (contentType.includes('rfc822') || blobPath.endsWith('.eml')) return 'email';
    if (contentType.includes('html') || url.includes('.html') || /\.html?$/i.test(blobPath)) return 'html';
    if (contentType.includes('json') || blobPath.endsWith('.json')) return 'json';
    if (contentType.includes('xml') || blobPath.endsWith('.xml')) return 'xml';
    if (contentType.includes('markdown') || blobPath.endsWith('.md')) return 'markdown';
    if (contentType.includes('text') || url.includes('.txt') || blobPath.endsWith('.txt')) return 'text';

    return 'unknown';
  };

  const documentType = getDocumentType();
  const isPdf = documentType === 'pdf';

  // Type-based colors and icons
  const getTypeConfig = () => {
    const type = citation.type || 'file';
    switch (type) {
      case 'law':
        return { 
          accent: 'blue', 
          icon: <PiScalesLight className="h-5 w-5 text-info-600" />, 
          bg: 'from-info-50 to-info-100',
          badge: 'bg-info-50 text-info-700 ring-1 ring-info-100'
        };
      case 'case':
        return { 
          accent: 'indigo', 
          icon: <PiBookOpenLight className="h-5 w-5 text-indigo-600" />, 
          bg: 'from-indigo-50 to-indigo-100', 
          badge: 'bg-indigo-50 text-indigo-700 ring-1 ring-indigo-100'
        };
      default:
        return { 
          accent: 'gray', 
          icon: <PiFileLight className="h-5 w-5 text-gray-600" />, 
          bg: 'from-gray-50 to-gray-100',
          badge: 'bg-gray-50 text-gray-700 ring-1 ring-gray-200'
        };
    }
  };

  const typeConfig = getTypeConfig();
  const accentClass = `text-${typeConfig.accent}-600 dark:text-${typeConfig.accent}-400`;

  // Loading state is based on whether we have the blob/fileSource data from streaming
  // It's not based on iframe onLoad (that's handled separately)
  useEffect(() => {
    // Once we have fileSource, we're done loading the data
    // The iframe will handle its own rendering
    if (fileSource) {
      setIsLoading(false);
    }
  }, [fileSource]);

  // If citation has metadata (title/summary) but no blob/URL, streaming either failed or
  // is not applicable (e.g. laws/cases with only external_url). Stop showing the loader.
  // BUT: if a blob_path exists, streaming is expected - keep showing the skeleton until
  // the blob arrives or a longer timeout elapses (the stream can take 10+ seconds).
  useEffect(() => {
    const hasMetadata = citation.title || citation.name || citation.summary;
    const hasFileData = citation.blob || citation.blobObject || citation.blob_url || citation.cdn_url;
    const expectsStreaming = !!(citation.blob_path || citation.blobPath);
    if (hasMetadata && !hasFileData && !fileSource) {
      // A fresh load is in flight - keep the loader up until it settles.
      setStreamGaveUp(false);
      // If a blob_path exists, the backend is streaming - use a longer timeout
      const delay = expectsStreaming ? 30000 : 2000;
      const timeout = setTimeout(() => {
        setIsLoading(false);
        setStreamGaveUp(true); // only now is the empty "No content" state allowed
      }, delay);
      return () => clearTimeout(timeout);
    }
  }, [citation.title, citation.name, citation.summary, citation.blob, citation.blobObject, citation.blob_url, citation.cdn_url, citation.blob_path, citation.blobPath, fileSource]);

  useEffect(() => {
    const country = citation.country?.toLowerCase();
    const docType = getDocumentType();
    // Skip if this is a JSON/XML/markdown doc - those have their own useEffects
    if (['json', 'xml', 'markdown'].includes(docType)) return;
    const isHtmlSource = country === 'no' || country === 'se' || docType === 'html';
    if (!isHtmlSource || !(citation.blob || citation.blobObject)) return;

    const blob = citation.blob || citation.blobObject;
    blob.text().then((text: string) => {
      if (country === 'no') {
        // Norwegian legal HTML parser (lovdata.no structure)
        const parser = new DOMParser();
        const doc = parser.parseFromString(text, 'text/html');
        const main = doc.querySelector('main#dokument');
        let contentHtml = main ? main.innerHTML : text;

        // Also extract metadata from the header if present
        const header = doc.querySelector('header.documentHeader');
        let metadataHtml = '';
        if (header) {
          const title = header.querySelector('dd.title')?.textContent || '';
          const shortTitle = header.querySelector('dd.titleShort')?.textContent || '';
          const ministry = header.querySelector('dd.ministry')?.textContent?.trim() || '';
          const dateInForce = header.querySelector('dd.dateInForce')?.textContent || '';
          const legacyId = header.querySelector('dd.legacyID')?.textContent || '';

          if (title || shortTitle || ministry || dateInForce || legacyId) {
            metadataHtml = `
              <div class="nr-metadata">
                ${shortTitle ? `<div class="nr-metadata-short-title">${shortTitle}</div>` : ''}
                ${legacyId ? `<div class="nr-metadata-id">${legacyId}</div>` : ''}
                <div class="nr-metadata-details">
                  ${ministry ? `<span>${ministry}</span>` : ''}
                  ${dateInForce ? `<span>I kraft: ${dateInForce}</span>` : ''}
                </div>
              </div>
            `;
          }
        }

        const style = `
          <style>
            /* ──────── RESET & BASE ──────── */
            .norway-reader * { font-family: inherit; box-sizing: border-box; }
            .norway-reader {
              font-size: 0.875rem;
              line-height: 1.7;
              color: #1a1a1a;
              max-width: 100%;
              padding: 0 0.5rem;
            }
            .dark .norway-reader { color: #e5e7eb; }

            /* ──────── METADATA BANNER ──────── */
            .norway-reader .nr-metadata {
              background: #fafafa;
              border: 1px solid #e5e5e5;
              border-radius: 6px;
              padding: 10px 14px;
              margin-bottom: 20px;
              font-size: 0.8rem;
            }
            .dark .norway-reader .nr-metadata {
              background: #1e1e1e;
              border-color: #333;
            }
            .norway-reader .nr-metadata-short-title {
              font-weight: 600;
              font-size: 0.85rem;
              color: #111;
              margin-bottom: 2px;
            }
            .dark .norway-reader .nr-metadata-short-title { color: #e0e0e0; }
            .norway-reader .nr-metadata-id {
              font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, monospace;
              font-size: 0.75rem;
              color: #666;
              margin-bottom: 4px;
            }
            .dark .norway-reader .nr-metadata-id { color: #999; }
            .norway-reader .nr-metadata-details {
              display: flex;
              flex-wrap: wrap;
              gap: 12px;
              font-size: 0.75rem;
              color: #888;
            }
            .dark .norway-reader .nr-metadata-details { color: #777; }

            /* ──────── HEADINGS ──────── */
            .norway-reader h1 {
              font-size: 1.25rem;
              font-weight: 700;
              line-height: 1.3;
              color: #111;
              margin: 0 0 16px 0;
              padding-bottom: 10px;
              border-bottom: 1px solid #e0e0e0;
            }
            .dark .norway-reader h1 { color: #f3f4f6; border-bottom-color: #374151; }
            .norway-reader h2 {
              font-size: 1.05rem;
              font-weight: 600;
              color: #222;
              margin: 28px 0 12px 0;
              padding: 8px 12px;
              background: #f5f5f5;
              border-left: 3px solid #999;
              border-radius: 0 4px 4px 0;
            }
            .dark .norway-reader h2 {
              color: #d1d5db;
              background: #1f2937;
              border-left-color: #6b7280;
            }
            .norway-reader h3 {
              font-size: 0.95rem;
              font-weight: 600;
              color: #333;
              margin: 20px 0 8px 0;
            }
            .dark .norway-reader h3 { color: #d1d5db; }

            /* ──────── LEGAL ARTICLES (§) ──────── */
            .norway-reader .legalArticle {
              margin: 16px 0;
              padding: 0;
            }
            .norway-reader .legalArticleHeader {
              font-size: 0.9rem;
              font-weight: 600;
              color: #222;
              margin: 0 0 6px 0;
              padding: 6px 10px;
              background: #f8f8f8;
              border-radius: 4px;
              border: 1px solid #eee;
            }
            .dark .norway-reader .legalArticleHeader {
              color: #e5e7eb;
              background: #1f2937;
              border-color: #374151;
            }
            .norway-reader .legalArticleValue {
              color: #444;
              font-weight: 700;
            }
            .dark .norway-reader .legalArticleValue { color: #d1d5db; }
            .norway-reader .legalArticleTitle {
              font-weight: 500;
            }

            /* ──────── PARAGRAPHS ──────── */
            .norway-reader .legalP,
            .norway-reader article.legalP {
              display: block;
              margin: 6px 0;
              padding: 0 0 0 10px;
              line-height: 1.7;
            }
            .norway-reader .defaultP,
            .norway-reader article.defaultP {
              display: block;
              margin: 10px 0 4px 0;
              padding: 0 0 0 10px;
            }
            .norway-reader .defaultP i,
            .norway-reader article.defaultP i {
              font-style: italic;
              font-weight: 500;
              color: #555;
              font-size: 0.85rem;
            }
            .dark .norway-reader .defaultP i,
            .dark .norway-reader article.defaultP i { color: #9ca3af; }

            /* ──────── LISTS ──────── */
            .norway-reader ul.defaultList {
              list-style: none;
              margin: 6px 0 6px 10px;
              padding: 0;
            }
            .norway-reader ul.defaultList > li {
              position: relative;
              padding: 2px 0 2px 18px;
              margin: 2px 0;
            }
            .norway-reader ul.defaultList > li::before {
              content: "–";
              position: absolute;
              left: 0;
              color: #999;
              font-weight: 400;
            }
            .norway-reader ul.defaultList > li .listArticle {
              display: block;
            }
            .norway-reader ul.defaultList > li .listArticle .legalP {
              padding-left: 0;
              margin: 0;
            }

            /* ──────── TABLE OF CONTENTS ──────── */
            .norway-reader ul.tocTopUl,
            .norway-reader ul.tocSubUl {
              list-style: none;
              margin: 0;
              padding: 0;
            }
            .norway-reader ul.tocTopUl {
              background: #fafafa;
              border: 1px solid #e5e5e5;
              border-radius: 6px;
              padding: 14px;
              margin: 10px 0;
            }
            .dark .norway-reader ul.tocTopUl {
              background: #1f2937;
              border-color: #374151;
            }
            .norway-reader ul.tocTopUl > li {
              font-weight: 600;
              font-size: 0.85rem;
              color: #222;
            }
            .dark .norway-reader ul.tocTopUl > li { color: #e5e7eb; }
            .norway-reader ul.tocSubUl {
              margin-left: 16px;
              margin-top: 4px;
            }
            .norway-reader ul.tocSubUl > li {
              font-weight: 400;
              font-size: 0.8rem;
              color: #555;
              padding: 2px 0;
              cursor: pointer;
            }
            .dark .norway-reader ul.tocSubUl > li { color: #9ca3af; }
            .norway-reader ul.tocSubUl > li:hover {
              color: #111;
            }
            .dark .norway-reader ul.tocSubUl > li:hover { color: #e5e7eb; }

            /* ──────── TABLES ──────── */
            .norway-reader table {
              width: 100%;
              border-collapse: collapse;
              margin: 10px 0;
              font-size: 0.8rem;
              border: 1px solid #e0e0e0;
              border-radius: 6px;
              overflow: hidden;
            }
            .dark .norway-reader table { border-color: #374151; }
            .norway-reader thead {
              background: #f5f5f5;
            }
            .dark .norway-reader thead { background: #1f2937; }
            .norway-reader thead tr:first-child th[colspan] {
              background: #eee;
              font-size: 0.85rem;
              font-weight: 600;
              color: #333;
              padding: 8px 12px;
              text-align: left;
            }
            .dark .norway-reader thead tr:first-child th[colspan] {
              background: #1f2937;
              color: #d1d5db;
            }
            .norway-reader th {
              padding: 8px 12px;
              text-align: left;
              font-weight: 600;
              font-size: 0.78rem;
              color: #333;
              border-bottom: 1px solid #ddd;
            }
            .dark .norway-reader th { color: #d1d5db; border-bottom-color: #4b5563; }
            .norway-reader td {
              padding: 6px 12px;
              border-bottom: 1px solid #f0f0f0;
              color: #333;
              vertical-align: top;
            }
            .dark .norway-reader td { border-bottom-color: #374151; color: #d1d5db; }
            .norway-reader tbody tr:hover {
              background: #fafafa;
            }
            .dark .norway-reader tbody tr:hover { background: #1f2937; }
            .norway-reader tbody tr.startGroup td {
              border-top: 1px solid #ddd;
              padding-top: 10px;
            }
            .dark .norway-reader tbody tr.startGroup td { border-top-color: #4b5563; }
            .norway-reader td i,
            .norway-reader th i {
              font-style: italic;
            }

            /* ──────── FOOTNOTES ──────── */
            .norway-reader footer.footnotes {
              margin-top: 16px;
              padding-top: 12px;
              border-top: 1px solid #e0e0e0;
            }
            .dark .norway-reader footer.footnotes { border-top-color: #374151; }
            .norway-reader .footnote {
              display: block;
              font-size: 0.8rem;
              color: #777;
              margin: 4px 0;
              padding-left: 12px;
            }
            .dark .norway-reader .footnote { color: #9ca3af; }
            .norway-reader .footnoteLabel {
              font-weight: 700;
              color: #555;
              margin-right: 4px;
            }
            .dark .norway-reader .footnoteLabel { color: #d1d5db; }
            .norway-reader sup.footnotereference {
              font-size: 0.65rem;
              color: #555;
              cursor: help;
              font-weight: 600;
            }
            .dark .norway-reader sup.footnotereference { color: #9ca3af; }

            /* ──────── SECTIONS ──────── */
            .norway-reader section.section {
              margin: 24px 0;
              padding: 0;
            }

            /* ──────── DEFINITION LISTS (metadata) ──────── */
            .norway-reader dl {
              margin: 8px 0;
            }
            .norway-reader dt {
              font-weight: 600;
              color: #888;
              font-size: 0.75rem;
              text-transform: uppercase;
              letter-spacing: 0.05em;
              margin-top: 8px;
            }
            .dark .norway-reader dt { color: #9ca3af; }
            .norway-reader dd {
              margin: 2px 0 8px 0;
              color: #222;
            }
            .dark .norway-reader dd { color: #e5e7eb; }

            /* ──────── LINKS ──────── */
            .norway-reader a {
              color: #555;
              text-decoration: underline;
              text-underline-offset: 2px;
              text-decoration-color: #ccc;
              transition: color 0.15s, text-decoration-color 0.15s;
            }
            .norway-reader a:hover {
              color: #111;
              text-decoration-color: #666;
            }
            .dark .norway-reader a { color: #9ca3af; text-decoration-color: #555; }
            .dark .norway-reader a:hover { color: #e5e7eb; text-decoration-color: #999; }

            /* ──────── MISC ──────── */
            .norway-reader strong { font-weight: 600; }
            .norway-reader i { font-style: italic; }
            .norway-reader .data-document-key-info { display: none; }

            /* ──────── SCROLL TARGET HIGHLIGHT ──────── */
            .norway-reader .nr-scroll-highlight {
              animation: nr-highlight 2s ease-out;
            }
            @keyframes nr-highlight {
              0% { background-color: #f0f0f0; outline: 2px solid #ccc; }
              100% { background-color: transparent; outline: 2px solid transparent; }
            }
            .dark .norway-reader .nr-scroll-highlight {
              animation: nr-highlight-dark 2s ease-out;
            }
            @keyframes nr-highlight-dark {
              0% { background-color: #374151; outline: 2px solid #4b5563; }
              100% { background-color: transparent; outline: 2px solid transparent; }
            }
          </style>
        `;
        setDocumentHtml(style + '<div class="norway-reader">' + metadataHtml + contentHtml + '</div>');
      } else {
        // Swedish / generic legal HTML (lagen.nu, wetten.overheid.nl, ...):
        // strip site chrome, keep the main content, and make links open in a
        // new tab (absolutized against the source origin).
        let contentHtml = sanitizeSourceHtml(text, sourceOrigin) || text;

        const style = `
          <style>
            .se-reader * { font-family: inherit; box-sizing: border-box; }
            .se-reader {
              font-size: 0.875rem;
              line-height: 1.7;
              color: #1a1a1a;
              max-width: 100%;
              padding: 0 0.5rem;
            }
            .dark .se-reader { color: #e5e7eb; }
            .se-reader h1 {
              font-size: 1.25rem; font-weight: 700; line-height: 1.3; color: #111;
              margin: 0 0 16px 0; padding-bottom: 10px;
              border-bottom: 1px solid #e0e0e0;
            }
            .dark .se-reader h1 { color: #f3f4f6; border-bottom-color: #374151; }
            .se-reader h2 {
              font-size: 1.05rem; font-weight: 600; color: #222;
              margin: 28px 0 12px 0; padding: 8px 12px;
              background: #f5f5f5; border-left: 3px solid #3b82f6; border-radius: 0 4px 4px 0;
            }
            .dark .se-reader h2 { color: #d1d5db; background: #1f2937; border-left-color: #60a5fa; }
            .se-reader h3 {
              font-size: 0.95rem; font-weight: 600; color: #333; margin: 20px 0 8px 0;
            }
            .dark .se-reader h3 { color: #d1d5db; }
            .se-reader p { margin: 6px 0; line-height: 1.7; }
            .se-reader a {
              color: #555; text-decoration: underline;
              text-underline-offset: 2px; text-decoration-color: #ccc;
            }
            .se-reader a:hover { color: #111; text-decoration-color: #666; }
            .dark .se-reader a { color: #9ca3af; text-decoration-color: #555; }
            .dark .se-reader a:hover { color: #e5e7eb; }
            .se-reader table {
              width: 100%; border-collapse: collapse; margin: 10px 0;
              font-size: 0.8rem; border: 1px solid #e0e0e0;
            }
            .dark .se-reader table { border-color: #374151; }
            .se-reader th {
              padding: 8px 12px; text-align: left; font-weight: 600;
              font-size: 0.78rem; color: #333; border-bottom: 1px solid #ddd;
              background: #f5f5f5;
            }
            .dark .se-reader th { color: #d1d5db; border-bottom-color: #4b5563; background: #1f2937; }
            .se-reader td {
              padding: 6px 12px; border-bottom: 1px solid #f0f0f0;
              color: #333; vertical-align: top;
            }
            .dark .se-reader td { border-bottom-color: #374151; color: #d1d5db; }
            .se-reader ul, .se-reader ol { margin: 6px 0 6px 20px; padding: 0; }
            .se-reader li { margin: 4px 0; }
            .se-reader dl { margin: 8px 0; }
            .se-reader dt { font-weight: 600; color: #555; margin-top: 8px; }
            .dark .se-reader dt { color: #9ca3af; }
            .se-reader dd { margin: 2px 0 8px 0; color: #222; }
            .dark .se-reader dd { color: #e5e7eb; }
            .se-reader strong { font-weight: 600; }
            .se-reader i, .se-reader em { font-style: italic; }
            .se-reader img { display: none; }
          </style>
        `;
        setDocumentHtml(style + '<div class="se-reader">' + contentHtml + '</div>');
      }
    }).catch((error: any) => {
        console.error('Error parsing document:', error);
        setDocumentHtml('');
    });
  }, [citation.blob, citation.blobObject, citation.country, sourceOrigin]);

  // Handle JSON documents - render structured legal data as readable HTML
  useEffect(() => {
    const isJsonDoc = documentType === 'json' && (citation.blob || citation.blobObject);
    if (!isJsonDoc) return;

    const blob = citation.blob || citation.blobObject;
    blob.text().then((text: string) => {
      try {
        const data = JSON.parse(text);

        // Build readable HTML from the JSON legal document
        const escapeHtml = (str: string) =>
          String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

        // Check if a string contains HTML tags (e.g. <p>, <br>, <strong>)
        const containsHtml = (str: string) => /<[a-z][a-z0-9]*[\s>\/]/i.test(str);

        // Sanitize HTML - allow only safe tags, strip everything else
        const sanitizeHtml = (str: string) => {
          const allowedTags = /^(p|br|strong|b|em|i|u|ul|ol|li|h[1-6]|a|span|div|table|tr|td|th|thead|tbody|blockquote|pre|code|hr|sub|sup|dl|dt|dd)$/i;
          return str.replace(/<\/?([a-z][a-z0-9]*)\b[^>]*>/gi, (match, tag) => {
            return allowedTags.test(tag) ? match : '';
          }).replace(/<script[\s\S]*?<\/script>/gi, '');
        };

        // Render a string value - if it contains HTML, sanitize and render it; otherwise escape
        const renderString = (str: string) =>
          containsHtml(str) ? sanitizeHtml(str) : escapeHtml(str);

        const renderValue = (value: any, depth: number = 0): string => {
          if (value === null || value === undefined) return '';
          if (Array.isArray(value)) {
            if (value.length === 0) return '';
            // Check if array of strings
            if (value.every((v: any) => typeof v === 'string')) {
              return '<ul class="json-list">' + value.map((v: any) => `<li>${renderString(v)}</li>`).join('') + '</ul>';
            }
            // Array of objects
            return value.map((v: any) => renderValue(v, depth + 1)).join('');
          }
          if (typeof value === 'object') {
            return renderObject(value, depth + 1);
          }
          return renderString(String(value));
        };

        const LABEL_MAP: Record<string, string> = {
          'sammanfattning': t('Summary'), 'innehall': t('Content'), 'rubrik': t('Title'),
          'domstolKod': t('Court Code'), 'domstolNamn': t('Court'), 'malNummerLista': t('Case Numbers'),
          'lagrumLista': t('Legal References'), 'litteraturLista': t('Literature'),
          'nyckelordLista': t('Keywords'), 'publiceringstid': t('Published'), 'typ': t('Type'),
          'referatNummerLista': t('Report Numbers'), 'rattsmradeLista': t('Legal Areas'),
          'arVagledande': t('Is Guiding'), 'arbetsdomstolenDomsnummer': t('AD Case No.'),
          'bilagaLista': t('Attachments'), 'forarbeteLista': t('Preparatory Works'),
          'gruppKorrelationsnummer': t('Correlation ID'), 'hanvisadePubliceringarLista': t('Related Refs'),
          'blob_path': '', 'id': '',
        };

        const renderObject = (obj: any, depth: number = 0): string => {
          let html = '';
          for (const [key, val] of Object.entries(obj)) {
            if (val === null || val === undefined || val === '') continue;
            if (Array.isArray(val) && val.length === 0) continue;
            const label = LABEL_MAP[key] !== undefined ? LABEL_MAP[key] : key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase());
            if (label === '') continue; // skip hidden fields

            const rendered = renderValue(val, depth);
            if (!rendered) continue;

            // Large text fields get their own section
            if (typeof val === 'string' && val.length > 200) {
              html += `<div class="json-section"><h3 class="json-label">${escapeHtml(label)}</h3><div class="json-content json-long-text">${renderString(val)}</div></div>`;
            } else if (typeof val === 'object') {
              html += `<div class="json-section"><h3 class="json-label">${escapeHtml(label)}</h3><div class="json-content">${rendered}</div></div>`;
            } else {
              html += `<div class="json-field"><span class="json-field-label">${escapeHtml(label)}</span><span class="json-field-value">${renderString(String(val))}</span></div>`;
            }
          }
          return html;
        };

        const contentHtml = renderObject(data);
        setDocumentHtml(STRUCTURED_READER_STYLE + '<div class="json-reader">' + contentHtml + '</div>');
      } catch (e) {
        // Not valid JSON - show as plain text
        const escaped = text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
        setDocumentHtml(`<pre style="white-space:pre-wrap;font-size:0.85rem;padding:1rem;line-height:1.6;color:#333;">${escaped}</pre>`);
      }
    }).catch(() => setDocumentHtml(''));
  }, [citation.blob, citation.blobObject, documentType]);

  // Handle XML documents - render the element tree as readable labelled
  // sections/fields (same look as the JSON reader), not a raw markup dump.
  useEffect(() => {
    const isXmlDoc = documentType === 'xml' && (citation.blob || citation.blobObject);
    if (!isXmlDoc) return;

    const blob = citation.blob || citation.blobObject;
    blob.text().then((text: string) => {
      const escapeHtml = (str: string) =>
        String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

      // Fall back to a readable monospace dump when the XML can't be parsed.
      const rawDump = () => {
        const escaped = escapeHtml(text);
        setDocumentHtml(`<pre style="white-space:pre-wrap;font-size:0.8rem;padding:1rem;line-height:1.6;color:#333;font-family:ui-monospace,SFMono-Regular,Menlo,monospace;">${escaped}</pre>`);
      };

      let doc: Document;
      try {
        doc = new DOMParser().parseFromString(text, 'application/xml');
      } catch {
        rawDump();
        return;
      }
      if (doc.querySelector('parsererror') || !doc.documentElement) {
        rawDump();
        return;
      }

      // Some legal "XML" is really XHTML/HTML - render it as sanitised HTML.
      const rootTag = doc.documentElement.tagName.toLowerCase();
      if (rootTag === 'html' || rootTag === 'body') {
        const htmlDoc = new DOMParser().parseFromString(text, 'text/html');
        htmlDoc.querySelectorAll('script, style, form, nav, header, footer, input, button').forEach(el => el.remove());
        const body = htmlDoc.body;
        setDocumentHtml('<div class="se-reader" style="padding:0.5rem;font-size:0.875rem;line-height:1.7;">' + (body ? body.innerHTML : escapeHtml(text)) + '</div>');
        return;
      }

      // Prettify an XML tag/attribute name into a human label.
      const prettify = (name: string) =>
        name.replace(/[_:.-]+/g, ' ')
            .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
            .replace(/\s+/g, ' ')
            .trim()
            .replace(/^./, s => s.toUpperCase());

      const allByLocal = (name: string): Element[] =>
        Array.from(doc.getElementsByTagName('*')).filter(e => e.localName === name);

      // ── Dutch rechtspraak.nl schema (open-rechtspraak) - render as a legal
      // document: metadata card + inhoudsindicatie + the judgment's paragraphs. ──
      const isRechtspraak = rootTag === 'open-rechtspraak' || allByLocal('uitspraak').length > 0 || allByLocal('inhoudsindicatie').length > 0;
      if (isRechtspraak) {
        const RDFS_NS = 'http://www.w3.org/2000/01/rdf-schema#';
        const labelOf = (el: Element) => el.getAttributeNS(RDFS_NS, 'label') || el.getAttribute('rdfs:label') || '';

        // Metadata: any rdf:Description child that carries a human rdfs:label,
        // plus the ECLI identifier and the document title. Deduped by label.
        const metaFields: Array<{ label: string; value: string }> = [];
        const seenLabels = new Set<string>();
        const pushMeta = (label: string, value: string) => {
          const l = label.trim(), v = value.trim();
          if (!l || !v || seenLabels.has(l.toLowerCase())) return;
          seenLabels.add(l.toLowerCase());
          metaFields.push({ label: l, value: v });
        };
        // ECLI + title first.
        const ecli = allByLocal('identifier').map(e => (e.textContent || '').trim()).find(t => /^ECLI:/i.test(t));
        if (ecli) pushMeta('ECLI', ecli);
        allByLocal('title').forEach(e => pushMeta('Titel', e.textContent || ''));
        // Then every labelled descriptor.
        Array.from(doc.getElementsByTagName('*')).forEach(el => {
          const lbl = labelOf(el);
          if (lbl) pushMeta(lbl, el.textContent || '');
        });

        const metaHtml = metaFields.length
          ? '<div class="rs-meta">' + metaFields.map(f =>
              `<div class="rs-meta-row"><span class="rs-meta-label">${escapeHtml(f.label)}</span><span class="rs-meta-value">${escapeHtml(f.value)}</span></div>`
            ).join('') + '</div>'
          : '';

        // Render a <para> as a paragraph; short numbered lines become headings.
        const renderPara = (el: Element): string => {
          const txt = (el.textContent || '').replace(/\s+/g, ' ').trim();
          if (!txt) return '';
          if (/^\d+(\.\d+)*\.?\s/.test(txt) && txt.length < 70) {
            return `<h3 class="rs-heading">${escapeHtml(txt)}</h3>`;
          }
          return `<p class="rs-para">${escapeHtml(txt)}</p>`;
        };
        // Render a node's paragraph content (para / parablock / nested).
        const renderBody = (root: Element): string => {
          let html = '';
          for (const child of Array.from(root.children)) {
            if (child.localName === 'parablock') {
              const inner = renderBody(child);
              if (inner) html += `<div class="rs-block">${inner}</div>`;
            } else if (child.localName === 'para') {
              html += renderPara(child);
            } else if (child.children.length) {
              html += renderBody(child);
            } else {
              html += renderPara(child);
            }
          }
          return html;
        };

        const summaryEl = allByLocal('inhoudsindicatie')[0];
        const summaryHtml = summaryEl && (summaryEl.textContent || '').trim()
          ? `<h2 class="rs-section-title">Inhoudsindicatie</h2>${renderBody(summaryEl)}`
          : '';
        const uitspraakEl = allByLocal('uitspraak')[0];
        const bodyHtml = uitspraakEl
          ? `<h2 class="rs-section-title">Uitspraak</h2>${renderBody(uitspraakEl)}`
          : '';

        const style = `<style>
          .rs-reader { font-size: 0.9rem; line-height: 1.7; color: #1a1a1a; max-width: 100%; padding: 0.5rem 0.75rem; }
          .dark .rs-reader { color: #e5e7eb; }
          .rs-reader .rs-meta { background: #fafafa; border: 1px solid #e5e5e5; border-radius: 8px; padding: 12px 14px; margin-bottom: 20px; }
          .dark .rs-reader .rs-meta { background: #1e1e1e; border-color: #333; }
          .rs-reader .rs-meta-row { display: flex; gap: 12px; padding: 4px 0; border-bottom: 1px solid #eee; font-size: 0.8rem; }
          .rs-reader .rs-meta-row:last-child { border-bottom: none; }
          .dark .rs-reader .rs-meta-row { border-bottom-color: #2a2a2a; }
          .rs-reader .rs-meta-label { font-weight: 600; color: #666; min-width: 130px; flex-shrink: 0; }
          .dark .rs-reader .rs-meta-label { color: #9ca3af; }
          .rs-reader .rs-meta-value { color: #222; word-break: break-word; }
          .dark .rs-reader .rs-meta-value { color: #e5e7eb; }
          .rs-reader .rs-section-title { font-size: 1.1rem; font-weight: 700; color: #111; margin: 24px 0 12px; padding-bottom: 6px; border-bottom: 2px solid #e0e0e0; }
          .dark .rs-reader .rs-section-title { color: #f3f4f6; border-bottom-color: #374151; }
          .rs-reader .rs-heading { font-size: 0.95rem; font-weight: 700; color: #222; margin: 20px 0 8px; }
          .dark .rs-reader .rs-heading { color: #d1d5db; }
          .rs-reader .rs-para { margin: 8px 0; text-align: justify; }
          .rs-reader .rs-block { margin: 8px 0 8px 16px; padding-left: 12px; border-left: 2px solid #eee; }
          .dark .rs-reader .rs-block { border-left-color: #374151; }
        </style>`;

        const contentHtml = metaHtml + summaryHtml + bodyHtml;
        if (contentHtml.trim()) {
          setDocumentHtml(style + '<div class="rs-reader">' + contentHtml + '</div>');
          return;
        }
      }

      const renderAttrs = (el: Element): string => {
        let html = '';
        for (const attr of Array.from(el.attributes)) {
          const val = (attr.value || '').trim();
          if (!val || attr.name.startsWith('xmlns')) continue;
          html += `<div class="json-field"><span class="json-field-label">${escapeHtml(prettify(attr.name))}</span><span class="json-field-value">${escapeHtml(val)}</span></div>`;
        }
        return html;
      };

      // Recursively render an element. Leaves become fields; containers become sections.
      const renderEl = (el: Element, depth: number): string => {
        const children = Array.from(el.children);
        const label = prettify(el.tagName);

        if (children.length === 0) {
          const val = (el.textContent || '').trim();
          const attrs = renderAttrs(el);
          if (!val && !attrs) return '';
          if (!val) {
            // Attribute-only leaf → a small section holding its attributes.
            return `<div class="json-section"><h3 class="json-label">${escapeHtml(label)}</h3><div class="json-content">${attrs}</div></div>`;
          }
          if (val.length > 200) {
            return `<div class="json-section"><h3 class="json-label">${escapeHtml(label)}</h3><div class="json-content json-long-text">${escapeHtml(val)}</div>${attrs}</div>`;
          }
          return `<div class="json-field"><span class="json-field-label">${escapeHtml(label)}</span><span class="json-field-value">${escapeHtml(val)}</span></div>${attrs}`;
        }

        const inner = renderAttrs(el) + children.map(c => renderEl(c, depth + 1)).join('');
        if (!inner) return '';
        // Don't wrap the root in a redundant heading.
        if (depth === 0) return inner;
        return `<div class="json-section"><h3 class="json-label">${escapeHtml(label)}</h3><div class="json-content">${inner}</div></div>`;
      };

      const contentHtml = renderEl(doc.documentElement, 0);
      if (!contentHtml.trim()) {
        rawDump();
        return;
      }
      setDocumentHtml(STRUCTURED_READER_STYLE + '<div class="json-reader">' + contentHtml + '</div>');
    }).catch(() => setDocumentHtml(''));
  }, [citation.blob, citation.blobObject, documentType]);

  // Handle Markdown documents - render as styled HTML
  useEffect(() => {
    const isMdDoc = documentType === 'markdown' && (citation.blob || citation.blobObject);
    if (!isMdDoc) return;

    const blob = citation.blob || citation.blobObject;
    blob.text().then((text: string) => {
      // Simple markdown-to-HTML conversion for common patterns
      let html = text
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
        // Headers
        .replace(/^### (.+)$/gm, '<h3>$1</h3>')
        .replace(/^## (.+)$/gm, '<h2>$1</h2>')
        .replace(/^# (.+)$/gm, '<h1>$1</h1>')
        // Bold / italic
        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.+?)\*/g, '<em>$1</em>')
        // Code blocks
        .replace(/```[\w]*\n([\s\S]*?)```/g, '<pre style="background:#f5f5f5;padding:12px;border-radius:4px;overflow-x:auto;font-size:0.8rem;">$1</pre>')
        // Inline code
        .replace(/`([^`]+)`/g, '<code style="background:#f0f0f0;padding:2px 4px;border-radius:3px;font-size:0.85em;">$1</code>')
        // List items
        .replace(/^- (.+)$/gm, '<li>$1</li>')
        .replace(/^(\d+)\. (.+)$/gm, '<li>$2</li>')
        // Horizontal rules
        .replace(/^---+$/gm, '<hr style="border:none;border-top:1px solid #e0e0e0;margin:16px 0;">')
        // Paragraphs (double newlines)
        .replace(/\n\n/g, '</p><p>')
        // Single newlines
        .replace(/\n/g, '<br>');

      const style = `<style>
        .md-reader { font-size:0.875rem; line-height:1.7; color:#1a1a1a; padding:0.5rem; }
        .dark .md-reader { color:#e5e7eb; }
        .md-reader h1 { font-size:1.25rem; font-weight:700; margin:0 0 12px; padding-bottom:8px; border-bottom:1px solid #e0e0e0; }
        .md-reader h2 { font-size:1.05rem; font-weight:600; margin:20px 0 8px; }
        .md-reader h3 { font-size:0.95rem; font-weight:600; margin:16px 0 6px; }
        .md-reader p { margin:6px 0; }
        .md-reader li { margin:4px 0 4px 20px; }
        .dark .md-reader h1 { border-bottom-color:#374151; }
      </style>`;
      setDocumentHtml(style + '<div class="md-reader"><p>' + html + '</p></div>');
    }).catch(() => setDocumentHtml(''));
  }, [citation.blob, citation.blobObject, documentType]);

  // Handle plain-text / unknown blobs - sniff the content so a mislabelled
  // JSON/XML/HTML blob still renders readably instead of as a raw iframe dump.
  useEffect(() => {
    const isTextLike = ['text', 'unknown'].includes(documentType) && (citation.blob || citation.blobObject);
    if (!isTextLike) return;

    const blob = citation.blob || citation.blobObject;
    blob.text().then((text: string) => {
      const trimmed = text.trimStart();
      const escapeHtml = (str: string) =>
        String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

      // Looks like JSON → try structured parse (best-effort; leave to the JSON
      // path's renderer would need refactor, so just pretty-print here).
      if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
        try {
          const pretty = JSON.stringify(JSON.parse(text), null, 2);
          setDocumentHtml(`<pre style="white-space:pre-wrap;font-size:0.8rem;padding:1rem;line-height:1.6;color:#333;font-family:ui-monospace,SFMono-Regular,Menlo,monospace;">${escapeHtml(pretty)}</pre>`);
          return;
        } catch { /* not valid JSON - fall through */ }
      }

      // Looks like HTML → sanitise and render.
      if (/^<!doctype html|^<html|<body[\s>]/i.test(trimmed)) {
        const htmlDoc = new DOMParser().parseFromString(text, 'text/html');
        htmlDoc.querySelectorAll('script, style, form, nav, header, footer, input, button').forEach(el => el.remove());
        setDocumentHtml('<div class="se-reader" style="padding:0.5rem;font-size:0.875rem;line-height:1.7;">' + (htmlDoc.body ? htmlDoc.body.innerHTML : escapeHtml(text)) + '</div>');
        return;
      }

      // Plain text → styled, readable block.
      setDocumentHtml(`<pre style="white-space:pre-wrap;font-size:0.875rem;padding:1rem;line-height:1.7;color:#333;font-family:inherit;">${escapeHtml(text)}</pre>`);
    }).catch(() => setDocumentHtml(''));
  }, [citation.blob, citation.blobObject, documentType]);

  // Handle Word documents with mammoth.js
  useEffect(() => {
    const isWordDocument = documentType === 'word' && (citation.blob || citation.blobObject);
    if (!isWordDocument) return;

    const convertWordDocument = async () => {
      setIsConvertingWord(true);
      try {
        const blob = citation.blob || citation.blobObject;
        const arrayBuffer = await blob.arrayBuffer();
        
        const mammoth = await import('mammoth');
        const result = await mammoth.convertToHtml(
          { arrayBuffer },
          {
            convertImage: mammoth.images.imgElement(function(image: any) {
              return image.read("base64").then(function(imageBuffer: string) {
                return { src: `data:${image.contentType};base64,${imageBuffer}` };
              });
            }),
            styleMap: [
              "p[style-name='Heading 1'] => h1.doc-heading:fresh",
              "p[style-name='Heading 2'] => h2.doc-heading:fresh",
              "p[style-name='Heading 3'] => h3.doc-heading:fresh",
              "p[style-name='Title'] => h1.doc-title:fresh",
              "b => strong",
              "i => em",
              "u => u"
            ]
          }
        );
        
        const styledHtml = `
          <style>
            .word-reader { font-family: "Times New Roman", Times, serif; font-size: 10pt; line-height: 1.4; color: #1a1a1a; padding-left: 20px; padding-right: 20px; }
            .word-reader img { max-width: 100%; height: auto; margin: 0.75em 0; }
            .word-reader table { border-collapse: collapse; width: 100%; margin: 0.75em 0; }
            .word-reader td, .word-reader th { border: 1px solid #ddd; padding: 6px; font-size: 10pt; font-weight: normal; }
            .word-reader h1, .word-reader h2, .word-reader h3, .word-reader h4, .word-reader h5, .word-reader h6 { color: #1a5490; }
            .word-reader h1, .word-reader h1.doc-heading { font-size: 18pt; margin-top: 0; margin-bottom: 10pt; font-weight: bold; }
            .word-reader h2, .word-reader h2.doc-heading { font-size: 14pt; margin-top: 14pt; margin-bottom: 5pt; font-weight: bold; }
            .word-reader h3, .word-reader h3.doc-heading { font-size: 12pt; margin-top: 12pt; margin-bottom: 4pt; font-weight: bold; }
            .word-reader p { margin: 0 0 10pt 0; }
            .word-reader ul, .word-reader ol { margin: 0 0 10pt 0; padding-left: 20pt; }
          </style>
          <div class="word-reader">${result.value}</div>
        `;
        
        setDocumentHtml(styledHtml);
        setIsLoading(false);
        setIsConvertingWord(false);
      } catch (error) {
        console.error('Error converting Word document:', error);
        setDocumentHtml('');
        setIsLoading(false);
        setIsConvertingWord(false);
      }
    };

    convertWordDocument();
  }, [citation.blob, citation.blobObject, documentType]);


  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr).toLocaleDateString(getIntlLocale(), { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      });
    } catch {
      return dateStr;
    }
  };

  // Scroll to a section in the document viewer by searching for matching text
  // Universal safety net: any link click inside rendered source HTML must open
  // in a new tab and NEVER navigate the SPA. In-page (#) anchors scroll within
  // the reader instead of touching the router.
  const handleContentClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const anchor = (e.target as HTMLElement).closest('a');
    if (!anchor) return;
    const href = anchor.getAttribute('href');
    if (!href) return;
    e.preventDefault();
    if (href.startsWith('#')) {
      const id = href.slice(1);
      const target = documentContentRef.current?.querySelector(`#${CSS.escape(id)}, [name="${CSS.escape(id)}"]`);
      target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    const abs = normalizeSourceUrl(absolutizeUrl(href, sourceOrigin));
    if (/^https?:/i.test(abs)) {
      window.open(abs, '_blank', 'noopener,noreferrer');
    }
  };

  const scrollToSection = (sectionText: string) => {
    const container = documentContentRef.current;
    if (!container) return;

    const normalizedSearch = sectionText.trim().toLowerCase();
    let targetEl: Element | null = null;

    // Helper: extract all § numbers from a section reference like "§§3–8" or "§3 og §4" or "§ 1"
    const extractParagrafNumbers = (text: string): string[] => {
      const nums: string[] = [];
      // Match §§X–Y or §§X-Y (range)
      const rangeMatch = text.match(/§§\s*(\d+)[–\-](\d+)/);
      if (rangeMatch) {
        const start = parseInt(rangeMatch[1]);
        const end = parseInt(rangeMatch[2]);
        for (let i = start; i <= end; i++) nums.push(String(i));
        return nums;
      }
      // Match individual § references: §3, § 3, §§3, etc.
      const individualMatches = text.matchAll(/§\s*(\d+)/g);
      for (const m of individualMatches) {
        if (!nums.includes(m[1])) nums.push(m[1]);
      }
      return nums;
    };

    // Helper: extract Kapittel number/roman numeral
    const extractKapittel = (text: string): string | null => {
      const match = text.match(/kapittel\s+([\divxlcm]+)/i);
      return match ? match[1].toLowerCase() : null;
    };

    // Strategy 1: Try to find § in the document
    const paragrafNums = extractParagrafNumbers(normalizedSearch);
    if (paragrafNums.length > 0) {
      const firstNum = paragrafNums[0];
      // Search for the § header element: look for .legalArticleValue containing "§ X"
      const allValues = container.querySelectorAll('.legalArticleValue, .legalArticleHeader');
      for (const el of allValues) {
        const elText = (el.textContent || '').replace(/\s+/g, ' ').trim();
        // Match "§ 3" or "§3" patterns
        if (elText.match(new RegExp(`§\\s*${firstNum}\\b`))) {
          targetEl = el.closest('.legalArticle') || el.closest('h3') || el;
          break;
        }
      }
      // Also try data-name attributes
      if (!targetEl) {
        targetEl = container.querySelector(`[data-name="§${firstNum}"]`) ||
                   container.querySelector(`[data-name="§ ${firstNum}"]`) ||
                   container.querySelector(`article[data-name*="§${firstNum}"]`);
      }
    }

    // Strategy 2: Try Kapittel match
    if (!targetEl) {
      const kap = extractKapittel(normalizedSearch);
      if (kap) {
        // Try roman numeral or number in headings
        const headings = container.querySelectorAll('h1, h2, h3, section[id], [data-name]');
        for (const el of headings) {
          const elText = (el.textContent || '').toLowerCase();
          if (elText.match(new RegExp(`kapittel\\s+${kap}\\b`, 'i'))) {
            targetEl = el;
            break;
          }
        }
        // Also try id-based
        if (!targetEl) {
          targetEl = container.querySelector(`#kapittel-${kap}`) ||
                     container.querySelector(`[data-name="kap${kap}"]`) ||
                     container.querySelector(`[id*="kapittel"]`);
        }
      }
    }

    // Strategy 3: Direct text match on headings and structural elements
    if (!targetEl) {
      // Try to extract a meaningful phrase - strip law references like "i voldsoffererstatningsloven"
      let searchPhrase = normalizedSearch
        .replace(/\s+i\s+[a-zæøå\-]+loven.*$/i, '')
        .replace(/\s+i\s+(lov|for|forskrift)\/[\d\-]+.*$/i, '')
        .trim();

      const allHeaders = container.querySelectorAll('h1, h2, h3, .legalArticleHeader, .legalArticleTitle, section > *:first-child');
      for (const el of allHeaders) {
        const elText = (el.textContent || '').trim().toLowerCase();
        if (elText.includes(searchPhrase) || searchPhrase.includes(elText)) {
          targetEl = el;
          break;
        }
      }
    }

    // Strategy 4: Text node search - walk the DOM to find matching text
    if (!targetEl) {
      const walker = document.createTreeWalker(
        container,
        NodeFilter.SHOW_TEXT,
        null
      );
      let node: Node | null;
      // Extract key words from the search (remove common Norwegian prepositions)
      const keywords = normalizedSearch
        .replace(/[()§,]/g, ' ')
        .split(/\s+/)
        .filter(w => w.length > 2 && !['og','i','av','for','til','fra','med','den','det','de','en','et','som','på'].includes(w));

      if (keywords.length > 0) {
        while ((node = walker.nextNode())) {
          const nodeText = (node.textContent || '').toLowerCase();
          const matchCount = keywords.filter(kw => nodeText.includes(kw)).length;
          if (matchCount >= Math.ceil(keywords.length * 0.6)) {
            // Found a text node with good keyword overlap
            targetEl = node.parentElement;
            break;
          }
        }
      }
    }

    if (targetEl) {
      targetEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
      // Highlight briefly with CSS class
      const el = targetEl as HTMLElement;
      el.classList.add('nr-scroll-highlight');
      setTimeout(() => {
        el.classList.remove('nr-scroll-highlight');
      }, 2200);
    }
  };

  /**
   * LAND ON THE PASSAGE, not at the top of the file.
   *
   * A datasource citation carries `locator: { strategy: "quote", quote }` and
   * nothing else - tenant files have no text-fragment offsets by design, so
   * searching the rendered document for the verbatim quote IS the anchor.
   * `scrollToSection` already does exactly that search (and flashes what it
   * finds); it was only ever reachable by clicking a section chip. This runs
   * it once, when the document that was opened for a quote has rendered.
   */
  const anchoredQuoteRef = useRef<string>('');
  useEffect(() => {
    const quote = String((citation as any)?.quote || '').trim();
    if (!quote || !documentHtml) return;
    const key = `${quote}::${documentHtml.length}`;
    if (anchoredQuoteRef.current === key) return;
    anchoredQuoteRef.current = key;
    // After paint: the container has to be in the DOM to be searched.
    const timer = setTimeout(() => scrollToSection(quote), 120);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentHtml, (citation as any)?.quote]);

  const ContentSection: React.FC<{
    title: string;
    content: string | string[];
    icon: React.ReactNode;
    className?: string;
    isSectionList?: boolean;
  }> = ({ title, content, icon, className = '', isSectionList = false }) => {
    const isArray = Array.isArray(content);

    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2 }}
        className={`space-y-3 ${className}`}
      >
        <div className="flex items-center gap-2">
          <div className={`p-1.5 rounded-full ${typeConfig.badge}`}>
            {icon}
          </div>
          <h4 className={`text-sm font-semibold ${accentClass} flex-1`}>{title}</h4>
          {isArray && <span className="text-xs px-2.5 py-1 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 font-medium">{content.length}</span>}
        </div>

        {isSectionList && isArray ? (
          <div className="flex flex-wrap gap-2">
            {(content as string[]).map((section: string, idx: number) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.15, delay: idx * 0.04 }}
                className={`inline-flex items-center px-3 py-1.5 rounded-md border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 transition-all duration-150 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer group`}
                onClick={() => scrollToSection(section)}
                title={t('Click to scroll to this section')}
              >
                <span className="text-xs font-medium text-gray-600 dark:text-gray-300 group-hover:text-gray-900 dark:group-hover:text-gray-100 line-clamp-2">
                  {section}
                </span>
              </motion.div>
            ))}
          </div>
        ) : (
          <div className={`p-3 rounded-lg bg-surface-soft border ${typeConfig.badge.replace('ring-1', 'border')} overflow-y-auto prose prose-sm max-w-none`}>
            <div className="text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-wrap text-sm">
              {isArray ? (
                <ul className="space-y-2 list-none">
                  {(content as string[]).map((item: string, idx: number) => (
                    <li key={idx} className="flex items-start gap-2">
                      <span className="text-info-600 dark:text-info-400 mt-0.5 flex-shrink-0">•</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                content
              )}
            </div>
          </div>
        )}
      </motion.div>
    );
  };

  const handleDeleteSource = () => {
    dispatch(showModal({
      showModal: true,
      type: 'confirmation',
      message: t('Delete Source'),
      subMsg: t('This source will be permanently removed. This action cannot be undone.'),
      onConfirm: async () => {
        try {
          const sourceId = citation.source_id || citation.id;
          await dispatch(removeSourceFromMatter(matterId, sourceType, sourceId as string));
          dispatch(showFeedbackModal({
            showModal: true,
            message: t('Source deleted successfully'),
            modalType: 'success',
            duration: 3000
          }));
          if (onDelete) {
            onDelete();
          }
          dispatch(hideSidePanel());
        } catch (error) {
          console.error('Failed to delete source:', error);
          dispatch(showFeedbackModal({
            showModal: true,
            message: t('Failed to delete source'),
            modalType: 'error',
            duration: 3000
          }));
        }
        dispatch(hideModal());
      },
      onCancel: () => dispatch(hideModal()),
      icon: <PiTrashLight className="w-6 h-6 text-danger-600" />,
      isDest: true,
    }));
  };

  // A filed .eml is not streamed as raw bytes - it is decrypted and parsed by
  // the backend and rendered as a message (header block + sanitised body +
  // attachments). All hooks above have run, so this early return is safe.
  if (documentType === 'email') {
    return (
      <EmailMessageView
        blobPath={citation.blobPath || citation.blob_path || ''}
        subjectFallback={citation.title || citation.name}
        onClose={onClose}
      />
    );
  }

  // Show loading state while streaming file from backend
  const isStreamingData = !fileSource || (!citation.blob && !citation.blobObject);

  /* ── Document info ──────────────────────────────────────────────────
     What the panel used to answer: uploaded/created, last modified, size,
     source. Four facts in a 45%-wide column - a banner, not metadata. It now
     answers the questions actually asked of a document (who put it here, what
     kind of thing is it, where is it from, how relevant is it) in a narrow
     fixed column beside the document, through the shared Field primitives so
     it reads like every other detail surface. */

  const uploaderName = resolveUploader(citation);
  const uploadedStamp = documentUploadedAt(citation);

  const sizeLabel = citation.size ? formatBytes(Number(citation.size)) : '';

  /** Corpus first, document type second - "Legislation" beats "law". */
  const kindLabel = (() => {
    const corpus = String((citation as any).corpus || '');
    if (corpus && CORPUS_LABEL[corpus]) return t(CORPUS_LABEL[corpus]);
    if (citation.type === 'law') return t('Legislation');
    if (citation.type === 'case') return t('Case law');
    const documentType = (citation as any).documentType || (citation as any).document_type;
    return documentType && documentType !== 'general'
      ? t(String(documentType).replace(/_/g, ' '))
      : '';
  })();

  const originLabel = (citation as any).origin === 'uploaded'
    ? t('Uploaded')
    : (citation as any).origin
      ? t('Found by AI')
      : '';

  const jurisdictionLabel = citation.country
    ? (getJurisdictionDisplay(String(citation.country))?.label || String(citation.country).toUpperCase())
    : '';

  const languageLabel = (citation as any).language
    ? languageName(String((citation as any).language))
    : '';

  const relevanceLabel = typeof (citation as any).relevanceScore === 'number'
    ? `${Math.round((citation as any).relevanceScore * ((citation as any).relevanceScore <= 1 ? 100 : 1))}%`
    : '';

  /** Rendered in this order; anything the payload cannot answer is dropped. */
  const infoFacts: { label: string; value: string }[] = [
    { label: t('Uploaded by'), value: uploaderName },
    { label: uploadedStamp && citation.uploaded_at ? t('Uploaded') : t('Created'), value: uploadedStamp ? formatDateTime(uploadedStamp) : '' },
    { label: t('Last Modified'), value: citation.lastModified ? formatDate(citation.lastModified) : '' },
    { label: t('Size'), value: sizeLabel },
    { label: t('Type'), value: kindLabel },
    { label: t('Origin'), value: originLabel },
    { label: t('Jurisdiction'), value: jurisdictionLabel },
    { label: t('Language'), value: languageLabel },
    { label: t('Relevance'), value: relevanceLabel },
    { label: t('Source'), value: citation.source ? String(citation.source) : '' },
  ].filter((fact) => !!fact.value);

  // Determine if the left sidebar has any meaningful content to show
  const hasSidebarContent = !!(
    infoFacts.length || citation.id || citation.relevant_sections ||
    citation.summary || citation.justification || citation.practical_application ||
    citation.external_url || canEdit
  );

  if (isStreamingData && (isLoading || !streamGaveUp)) {
    return (
      <div className="flex flex-col h-full bg-white dark:bg-gray-900">
        {/* Compact top bar for loading state */}
        <div className="px-4 py-3 border-b border-gray-200/50 dark:border-gray-700/50 flex items-center gap-3">
          {onClose && (
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 transition-colors"
              onClick={onClose}
              title={t('Close')}
            >
              <PiXLight className="h-4 w-4" />
            </motion.button>
          )}
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <div className={`p-1.5 rounded-full ${typeConfig.badge}`}>
              {typeConfig.icon}
            </div>
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300 truncate">
              {citation.title || citation.name || t('Loading document...')}
            </span>
          </div>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <FileTypeSkeleton fileType={documentType as 'docx' | 'pdf' | 'excel' | 'text' | 'unknown'} />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-gradient-to-br from-white to-gray-50 dark:from-gray-900 dark:to-gray-800 overflow-hidden" ref={containerRef}>
      {/* Content area - sidebar + document OR full-width document */}
      <div className="flex flex-1 overflow-hidden">
      {hasSidebarContent && (
      /* A NARROW FIXED COLUMN, not a share of the width. At 45% the metadata
         took nearly half the reader on every screen, so the document - the
         thing being read - was squeezed hardest exactly where there was least
         room. Metadata has a natural width; the document should have the rest. */
      <div
        data-tour-id="reader-sidebar"
        className="w-[280px] lg:w-[320px] flex-shrink-0 border-e border-hairline bg-surface flex flex-col"
      >

        <div className="space-y-4 p-4 flex-1 overflow-y-auto custom-scrollbar-main">
          {!infoFacts.length && isLoading ? (
            <div className="pb-4 border-b border-hairline-soft">
              <Skeleton variant="card" rows={3} />
            </div>
          ) : infoFacts.length ? (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2 }}
              className="pb-4 border-b border-gray-200/50 dark:border-gray-700/50 space-y-3"
            >
              <div className="flex items-center gap-2">
                <div className={`p-1.5 rounded-full ${typeConfig.badge}`}>
                  <InformationCircleIcon className={`h-4 w-4 ${accentClass}`} />
                </div>
                <h4 className={`text-sm font-semibold ${accentClass}`}>{t('Document Info')}</h4>
              </div>
              <Well className="space-y-2">
                {infoFacts.map((fact) => (
                  <InfoRow key={fact.label} label={fact.label}>
                    <span
                      className="text-gray-700 dark:text-gray-200 text-sm text-end truncate max-w-[170px]"
                      title={fact.value}
                    >
                      {fact.value}
                    </span>
                  </InfoRow>
                ))}
              </Well>
            </motion.div>
          ) : null}
        {(citation.relevant_sections) && (
            <ContentSection
              title={t('Relevant Sections')}
              content={
                Array.isArray(citation.relevant_sections)
                  ? citation.relevant_sections.map((s: any) =>
                      typeof s === 'string' ? s : (s.section_name ? `${s.section_name}: ${s.section_content || ''}` : s.section_content || String(s))
                    )
                  : typeof citation.relevant_sections === 'string'
                    ? [citation.relevant_sections]
                    : []
              }
              icon={<PiParagraphLight className="h-4 w-4" />}
              isSectionList={true}
            />
          )}
          
      {citation.summary && (
          <ContentSection
            title={t('Summary')}
            content={citation.summary}
            icon={<PiClipboardTextLight className="h-4 w-4" />}
          />
        )}

        {citation.justification && (
          <ContentSection
            title={t('Justification')}
            content={citation.justification}
            icon={<PiChatCenteredTextLight className="h-4 w-4" />}  
          />
        )}

        {citation.practical_application && (
          <ContentSection
            title={t('Practical Application')}
            content={citation.practical_application}
            icon={<PiChatCenteredTextLight className="h-4 w-4" />}  
          />
        )}

          {citation.external_url && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2 }}
              className="pt-2"
            >
              <div className="flex items-center gap-2 mb-2">
                <div className={`p-1.5 rounded-full ${typeConfig.badge}`}>
                  <PiGlobeLight className={`h-4 w-4 ${accentClass}`} />
                </div>
                <h4 className={`text-sm font-semibold ${accentClass}`}>{t('Source Link')}</h4>
              </div>
              <div className="flex items-center gap-2 text-xs">
                <span className="text-gray-500 dark:text-gray-400 truncate flex-1" title={citation.external_url}>
                  {new URL(citation.external_url).hostname}
                </span>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className={`p-2 rounded-lg ${typeConfig.badge.replace('ring-1', 'bg hover:bg')} hover:text-${typeConfig.accent}-700 transition-colors text-xs`}
                  onClick={() => window.open(cleanExternalUrl(citation.external_url!), '_blank')}
                  title={t('Open Original')}
                >
                  <PiArrowSquareOutLight className="h-3 w-3" />
                </motion.button>
              </div>
            </motion.div>
          )}
        </div>
        <div data-tour-id="reader-actions" className="p-4 glass !border-x-0 !border-b-0 flex items-center justify-between gap-4 sticky bottom-0">
          {canEdit && (
            <Button
              variant="destructive"
              size="small"
              onClick={handleDeleteSource}
            >
              {t('Delete Source')}
            </Button>
          )}
        </div>
      </div>
      )}

      <div data-tour-id="reader-content" className="flex-1 min-w-0 relative bg-white dark:bg-gray-900 overflow-hidden">
        <div className="flex-1 overflow-auto custom-scrollbar-main" ref={pdfRef}>
          <AnimatePresence mode="wait">
            {fileSource ? (
              <motion.div
                key="document"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="h-[calc(100vh-110px)] flex flex-col custom-scrollbar-main"
              >
                <div className="flex-1 h-[calc(100vh-80px)]">
                  {isConvertingWord ? (
                    <div className="flex flex-col items-center justify-center h-full bg-gray-50 dark:bg-gray-800">
                      <Spinner size="lg" label={t('Processing document...')} />
                    </div>
                  ) : documentHtml ? (
                    <div ref={documentContentRef} onClick={handleContentClick} className="p-4 overflow-y-auto dark:text-gray-200" style={citation.country?.toUpperCase() === 'SE' ? { fontSize: '1.1em' } : undefined} dangerouslySetInnerHTML={{ __html: documentHtml }} />
                  ) : isPdf ? (
                    <iframe
                      src={`${fileSource}#toolbar=0&navpanes=0&scrollbar=0${citation.country?.toUpperCase() === 'SE' ? '&zoom=100' : ''}`}
                      className="w-full h-full border-none custom-scrollbar-main"
                      title={citation.title || citation.name}
                    />
                  ) : documentType === 'word' ? (
                    <div className="flex flex-col items-center justify-center h-full bg-gray-50 dark:bg-gray-800">
                      <Spinner size="lg" label={t('Processing document...')} />
                    </div>
                  ) : ['excel', 'powerpoint'].includes(documentType) ? (
                    <iframe
                      src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(fileSource || '')}`}
                      className="w-full border-none h-[calc(100vh-80px)]"
                      title={citation.title || citation.name}
                    />
                  ) : (
                    <iframe
                      src={fileSource || ''}
                      className="w-full h-[calc(100vh-80px)] border-none"
                      title={citation.title || citation.name}
                    />
                  )}
                </div>
              </motion.div>
            ) : (
              <motion.div
                key="empty"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="flex flex-col items-center justify-center h-full text-center space-y-4"
              >
                <div className={`p-4 rounded-full ${typeConfig.bg}`}>
                  {typeConfig.icon}
                </div>
                <div className="space-y-2">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                    {t('No content available')}
                  </h3>
                  <p className="text-gray-500 dark:text-gray-400 max-w-md">
                    {t('This document may not have extractable content. Check the summary above or view the original document.')}
                  </p>
                </div>
                {citation.external_url && (
                  <Button
                    onClick={() => window.open(cleanExternalUrl(citation.external_url!), '_blank')}
                    className={`m-1`}
                    variant='outline'
                    size='small'
                  >
                    {t('View Original Document')}
                  </Button>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
      </div>
      <FeatureTour
        steps={sourceReaderTourSteps}
        run={isTourRunning}
        onCallback={handleTourCallback}
        disableOverlay
      />
    </div>
  );
};

export default CustomFileReader;